import { callRpc } from "./client";
import { somenteCadastrosAtivos } from "../utils/cadastrosAtivos";

export interface ConfiguracaoGoogleMobile {
  clientId: string;
  androidClientId: string;
  iosClientId: string;
}

/** IDs públicos compartilhados entre as configurações Web e o login nativo. */
export function obterConfiguracaoGoogleMobile() {
  return callRpc<ConfiguracaoGoogleMobile>("obterGoogleClientIdLogin");
}

export function salvarConfiguracaoGoogleMobile(dados: Pick<ConfiguracaoGoogleMobile, "androidClientId" | "iosClientId">) {
  return callRpc<{ sucesso: boolean; erro?: string }>("salvarConfiguracaoGoogleMobile", dados);
}

/* ------------------- Dados gerais (Configurações) ------------------- */
export interface DadosGerais {
  dominioApi: string;
  fantasia: string;
  contato: string;
  email: string;
  cnpj: string;
  repLegal: string;
  linkMarketing: string;
  linkFacebook: string;
  linkInstagram: string;
  linkYoutube: string;
  linkLinkedin: string;
  linkSite: string;
  linkAppAndroid: string;
  linkAppIos: string;
  textoCopyright: string;
  nomeAgenteIA: string;
  instrucoesAgenteIA: string;
  [chave: string]: unknown;
}

export async function obterDadosGerais(): Promise<DadosGerais> {
  const res = await callRpc<DadosGerais>("obterDadosGeraisSistema");
  return res ?? ({} as DadosGerais);
}

export interface DadosEmpresaSistema {
  fantasia: string;
  contato: string;
  email: string;
  cnpj: string;
  repLegal: string;
  linkSite: string;
  logoAbertoId: string;
  logoAbertoWhiteId: string;
  /** Imagem exclusiva dos documentos fiscais e pedidos, independente do menu. */
  logoNotaFiscalId?: string;
  corBase: string;
}

export async function obterDadosEmpresa(codEmpresa?: number | string): Promise<DadosEmpresaSistema | null> {
  return await callRpc<DadosEmpresaSistema | null>("obterDadosEmpresaSistema", codEmpresa);
}

export function salvarDadosGerais(
  dados: Partial<DadosGerais>
): Promise<{ sucesso?: boolean; dados?: DadosGerais; erro?: string }> {
  return callRpc("salvarDadosGeraisSistema", dados);
}

/* ------------------- Configuração visual (cores/gradiente) ------------------- */
export interface ConfigVisual {
  fantasia?: string;
  corPerfil?: string;
  corLight?: string;
  marcaEmpresaPersonalizada?: boolean;
  statusGradiente?: string | boolean;
  observacoes?: string;
  intensidadeCor?: string;
  tonalidadeCor?: string;
  bgGradient?: string;
  inverterGradient?: string | boolean;
  botoesRetangulares?: string | boolean;
  glass?: string | boolean;
  glassIntensidade?: string | number;
  bgGradient1XY?: string;
  bgGradient2XY?: string;
  bgGradient1Vertical?: string | boolean;
  bgGradient2Vertical?: string | boolean;
  bgGradientSize?: string;
  bgGradient1X?: string | number;
  bgGradient1Y?: string | number;
  bgGradient1Size?: string | number;
  bgGradient2X?: string | number;
  bgGradient2Y?: string | number;
  bgGradient2Size?: string | number;
  corSidebar?: string;
  opacidadeSidebar?: string | number;
  modulosAtivos?: string;
  loginSocial?: string | boolean;
  cadastroLogin?: string | boolean;
  tourBoasVindas?: string | boolean;
  notaAtendimentoAtiva?: string | boolean | number;
  sessaoUnica?: string | boolean | number;
  logoutInatividade?: string | boolean | number;
  logoutInatividadeMinutos?: string | number;
  empresaDestinoCompras?: string | number | null;
  iaAtiva?: string | boolean;
  configGraficos?: ConfiguracaoGraficos;
  [chave: string]: unknown;
}

export type TipoGrafico =
  | "line" | "bar" | "radar" | "pie" | "doughnut" | "polarArea" | "bubble" | "scatter" | "horizontalBar" | "liquidGauge"
  | "gauge" | "gaugeSemicircular" | "gaugeVelocimetro" | "gaugeAnel" | "roscaModerna" | "barraPolar" | "barraPolarRaio";

export interface ConfiguracaoItensGrafico {
  rotulos: boolean;
  legendaPosicao: "top" | "bottom" | "left" | "right";
  marcadores: boolean;
}

export interface ConfiguracaoGraficos {
  tiposLiberados: TipoGrafico[];
  linha: {
    areaPreenchida: boolean;
    gradienteArea: boolean;
    curvaSuave: boolean;
  };
  barra: {
    empilhado: boolean;
    bordaArredondada: boolean;
    orientacao: "vertical" | "horizontal";
  };
  gaugeLiquido: {
    velocidadeOnda: number;
    amplitudeOnda: number;
    mostrarTexto: boolean;
  };
  /** Mantido como padrão/fallback para configurações salvas antes dos controles individuais. */
  itens: ConfiguracaoItensGrafico;
  itensPorTipo?: Partial<Record<TipoGrafico, ConfiguracaoItensGrafico>>;
}

const ITENS_GRAFICO_PADRAO: ConfiguracaoItensGrafico = {
  rotulos: true,
  legendaPosicao: "bottom",
  marcadores: true,
};

function criarItensPorTipoPadrao(): Record<TipoGrafico, ConfiguracaoItensGrafico> {
  return Object.fromEntries(
    [
      "line", "bar", "radar", "pie", "doughnut", "polarArea", "bubble", "scatter", "horizontalBar", "liquidGauge",
      "gauge", "gaugeSemicircular", "gaugeVelocimetro", "gaugeAnel", "roscaModerna", "barraPolar", "barraPolarRaio",
    ].map((tipo) => [tipo, { ...ITENS_GRAFICO_PADRAO }])
  ) as Record<TipoGrafico, ConfiguracaoItensGrafico>;
}

export const CONFIGURACAO_GRAFICOS_PADRAO: ConfiguracaoGraficos = {
  tiposLiberados: [
    "line", "bar", "radar", "pie", "doughnut", "polarArea", "bubble", "scatter", "horizontalBar", "liquidGauge",
    "gauge", "gaugeSemicircular", "gaugeVelocimetro", "gaugeAnel", "roscaModerna", "barraPolar", "barraPolarRaio",
  ],
  linha: { areaPreenchida: true, gradienteArea: true, curvaSuave: true },
  barra: { empilhado: false, bordaArredondada: true, orientacao: "horizontal" },
  gaugeLiquido: { velocidadeOnda: 3000, amplitudeOnda: 8, mostrarTexto: true },
  itens: { ...ITENS_GRAFICO_PADRAO },
  itensPorTipo: criarItensPorTipoPadrao(),
};

export function obterItensGrafico(
  config: ConfiguracaoGraficos,
  tipo: TipoGrafico
): ConfiguracaoItensGrafico {
  return config.itensPorTipo?.[tipo] ?? config.itens ?? ITENS_GRAFICO_PADRAO;
}

export function configuracaoGraficosPorTipo(
  config: ConfiguracaoGraficos,
  tipo: TipoGrafico
): ConfiguracaoGraficos {
  return { ...config, itens: obterItensGrafico(config, tipo) };
}

export async function obterConfigVisual(aplicarMarcaEmpresa = true): Promise<ConfigVisual> {
  const res = await callRpc<ConfigVisual>(
    "obterConfiguracoesVisuais",
    aplicarMarcaEmpresa ? undefined : "__config_banco__"
  );
  return res ?? {};
}

/**
 * Salva a cor de destaque e variantes.
 * Argumentos no backend: (cor_perfil, cor_light, intensidade_cor, tonalidade_cor, observacoes=gradIntensidade)
 */
export function salvarCorPerfil(
  cor: string,
  corLight: string,
  intensidade = "100",
  tonalidade = "0",
  gradIntensidade = "40"
): Promise<{ sucesso?: boolean; erro?: string }> {
  return callRpc("salvarCorPerfil", cor, corLight, intensidade, tonalidade, gradIntensidade);
}

/** Liga/desliga o fundo com gradiente. */
export function salvarGradiente(ativo: boolean): Promise<{ sucesso?: boolean; erro?: string }> {
  return callRpc("salvarConfiguracaoGradienteBanco", ativo);
}

/* ------------------- Módulos ------------------- */
export function salvarLiberacaoModulos(modulosStr: string, codOrganizacao?: number | string) {
  return callRpc("salvarLiberacaoModulos", modulosStr, codOrganizacao);
}

export interface ModulosOrganizacao {
  codOrganizacao: number;
  /** Catálogo da plataforma. */
  catalogo: string[];
  /** Ligados nesta organização. */
  ativos: string[];
}

/** Módulos de uma organização (sem o recorte da empresa da sessão). */
export async function obterModulosOrganizacao(codOrganizacao: number | string): Promise<ModulosOrganizacao> {
  const resposta = await callRpc<{ sucesso?: boolean; erro?: string; dados?: ModulosOrganizacao }>(
    "obterModulosOrganizacao",
    codOrganizacao
  );
  if (resposta?.sucesso === false) {
    throw new Error(resposta.erro || "Não foi possível carregar os módulos da organização.");
  }
  return resposta.dados as ModulosOrganizacao;
}

/* ------------------- Gráficos ------------------- */
export function salvarConfiguracaoGraficos(config: ConfiguracaoGraficos) {
  return callRpc<{ sucesso?: boolean; dados?: ConfigVisual }>("salvarConfiguracaoGraficos", config);
}

/* ------------------- Aparência avançada ------------------- */
export function salvarLoginSocial(ativo: boolean) {
  return callRpc("salvarLoginSocialBanco", ativo);
}
export function salvarCadastroLogin(ativo: boolean) {
  return callRpc("salvarCadastroLoginBanco", ativo);
}
export function salvarTourBoasVindas(ativo: boolean) {
  return callRpc("salvarTourBoasVindasBanco", ativo);
}
export function salvarNotaAtendimento(ativo: boolean) {
  return callRpc("salvarNotaAtendimentoBanco", ativo);
}
export function salvarControlesSessao(controles: { sessaoUnica: boolean; logoutInatividade: boolean; logoutInatividadeMinutos: number }) {
  return callRpc<{ sucesso: boolean; dados: ConfigVisual }>("salvarControlesSessaoBanco", controles);
}
/** O destino operacional é persistido no servidor; a seleção local não autoriza encaminhamentos. */
export async function salvarEmpresaDestinoCompras(codEmpresa: string) {
  const resposta = await callRpc<{ sucesso: boolean; erro?: string; dados?: ConfigVisual }>("salvarEmpresaDestinoComprasBanco", codEmpresa || null);
  if (!resposta.sucesso) throw new Error(resposta.erro || "Não foi possível salvar a empresa de destino.");
  return resposta;
}
export function salvarBgGradiente(gradiente: string) {
  return callRpc("salvarBgGradienteBanco", gradiente);
}
export function salvarInverterGradiente(inverter: boolean) {
  return callRpc("salvarInverterGradientBanco", inverter);
}
export function salvarBotoesRetangulares(ativo: boolean) {
  return callRpc("salvarBotoesRetangularesBanco", ativo);
}
/** Efeito Glass dos modais/popups: liga/desliga e a intensidade (0 a 100). */
export function salvarGlass(ativo: boolean, intensidade: number) {
  return callRpc("salvarGlassBanco", ativo, intensidade);
}
export function salvarBgGradientePos1(pos: number) {
  return callRpc("salvarBgGradient1XYBanco", String(pos));
}
export function salvarBgGradientePos2(pos: number) {
  return callRpc("salvarBgGradient2XYBanco", String(pos));
}
export function salvarBgGradientePos1Vertical(vertical: boolean) {
  return callRpc("salvarBgGradient1VerticalBanco", vertical);
}
export function salvarBgGradientePos2Vertical(vertical: boolean) {
  return callRpc("salvarBgGradient2VerticalBanco", vertical);
}
export function salvarBgGradienteSize(size: number) {
  return callRpc("salvarBgGradientSizeBanco", String(size));
}
export async function salvarConfiguracaoGradientes(configuracao: {
  gradiente1X: number;
  gradiente1Y: number;
  gradiente1Size: number;
  gradiente2X: number;
  gradiente2Y: number;
  gradiente2Size: number;
}) {
  const resposta = await callRpc<{ sucesso?: boolean; erro?: string }>(
    "salvarConfiguracaoGradientesBanco",
    configuracao.gradiente1X,
    configuracao.gradiente1Y,
    configuracao.gradiente1Size,
    configuracao.gradiente2X,
    configuracao.gradiente2Y,
    configuracao.gradiente2Size
  );
  if (resposta?.sucesso === false) {
    throw new Error(resposta.erro || "Configuração dos gradientes inválida.");
  }
  return resposta;
}
export async function salvarConfiguracaoSidebar(cor: string, opacidade: number) {
  const resposta = await callRpc<{ sucesso?: boolean; erro?: string }>(
    "salvarConfiguracaoSidebarBanco",
    cor,
    opacidade
  );
  if (resposta?.sucesso === false) {
    throw new Error(resposta.erro || "Configuração da sidebar inválida.");
  }
  return resposta;
}

/* ------------------- Chaves de API ------------------- */
export interface ChaveApiInfo {
  configured?: boolean;
  mask?: string;
  valid?: boolean;
  sanitized?: boolean;
}

export interface ChavesAPI {
  resend?: ChaveApiInfo;
  chatgpt?: ChaveApiInfo;
  google?: ChaveApiInfo;
}
export async function obterChavesAPI(): Promise<ChavesAPI> {
  const res = await callRpc<ChavesAPI>("obterChavesAPI");
  return res ?? {};
}
export function salvarChaveResend(chave: string) {
  return callRpc("salvarChaveResend", chave);
}
export function salvarChaveChatGpt(chave: string) {
  return callRpc("salvarChaveChatGpt", chave);
}
export function salvarChaveGoogle(chave: string) {
  return callRpc("salvarChaveGoogle", chave);
}
export interface ConsumoEmailOrganizacao {
  codOrganizacao: number;
  organizacao: string;
  totalGeral: number;
  totalUsado: number;
  totalLivre: number;
  emailsAtivo: boolean;
  limiteAtualizadoEm: string;
  totalClicado: number;
  totalEntregue: number;
  totalFalhou: number;
  totalRejeitado: number;
}

export type AmbienteEmail = "SANDBOX" | "PRODUCAO";

export interface ResumoEmailsOrganizacoes {
  sucesso?: boolean;
  erro?: string;
  podeConfigurar: boolean;
  ativo: boolean;
  resendFrom: string;
  resendReplyTo: string;
  resendTestTo: string;
  ambiente: AmbienteEmail;
  publicAppUrl: string;
  resendWebhookSecret: string;
  webhookSecretConfigurado: boolean;
  webhookSecretMask: string;
  webhookUrl: string;
  periodo: string;
  totalGeral: number;
  totalUsado: number;
  totalLivre: number;
  limiteEmailsDiario: number;
  totalEnviadoHoje: number;
  organizacoes: ConsumoEmailOrganizacao[];
}

export async function obterResumoEmailsOrganizacoes(): Promise<ResumoEmailsOrganizacoes> {
  const res = await callRpc<ResumoEmailsOrganizacoes>("obterResumoEmailsOrganizacoes");
  if (res?.sucesso === false) throw new Error(res.erro || "Erro ao carregar consumo de e-mails.");
  return res ?? {
    podeConfigurar: false,
    ativo: false,
    resendFrom: "",
    resendReplyTo: "",
    resendTestTo: "",
    ambiente: "SANDBOX",
    publicAppUrl: "",
    resendWebhookSecret: "",
    webhookSecretConfigurado: false,
    webhookSecretMask: "",
    webhookUrl: "",
    periodo: "",
    totalGeral: 0,
    totalUsado: 0,
    totalLivre: 0,
    limiteEmailsDiario: 100,
    totalEnviadoHoje: 0,
    organizacoes: [],
  };
}

export interface ConfiguracaoEmailEmpresa {
  codOrganizacao: number;
  organizacao: string;
  codEmpresa: number;
  empresa: string;
  saldoOrganizacao: number;
  saldoEmpresa: number;
  limiteDiario: number;
  enviadosHoje: number;
  usadosMes: number;
  ativo: boolean;
  imagemRodape: string;
}

export interface ResumoEmailsEmpresas {
  sucesso?: boolean;
  erro?: string;
  saldoResend: number;
  empresas: ConfiguracaoEmailEmpresa[];
}

export async function obterResumoEmailsEmpresas(): Promise<ResumoEmailsEmpresas> {
  const response = await callRpc<ResumoEmailsEmpresas>("obterResumoEmailsEmpresas");
  if (response?.sucesso === false) throw new Error(response.erro || "Não foi possível carregar os saldos de e-mail.");
  return response ?? { saldoResend: 0, empresas: [] };
}

export function salvarConfiguracaoEmailEmpresa(dados: {
  codOrganizacao: number;
  codEmpresa: number;
  saldoOrganizacao: number;
  saldoEmpresa: number;
  limiteDiario: number;
  ativo: boolean;
}) {
  return callRpc<{ sucesso?: boolean; erro?: string }>("salvarConfiguracaoEmailEmpresa", dados);
}

/**
 * O Resend não publica saldo por API: o total é um controle manual da
 * plataforma, editado direto no card de saldo.
 */
export function salvarSaldoResend(saldoResend: number) {
  return callRpc<{ sucesso?: boolean; erro?: string; saldoResend?: number }>("salvarSaldoEmailsResend", { saldoResend });
}

export function salvarRodapeEmailEmpresa(dados: {
  codOrganizacao: number;
  codEmpresa: number;
  base64: string;
}) {
  return callRpc<{ sucesso?: boolean; erro?: string; id?: string; url?: string }>("salvarRodapeEmailEmpresa", dados);
}

export function removerRodapeEmailEmpresa(dados: { codOrganizacao: number; codEmpresa: number }) {
  return callRpc<{ sucesso?: boolean; erro?: string }>("removerRodapeEmailEmpresa", dados);
}

export interface SerieEntregabilidadeEmailDia {
  dia: string;
  enviados: number;
  entregues: number;
  abertos: number;
  clicados: number;
  falhos: number;
  rejeitados: number;
  reclamados: number;
  atrasados: number;
}

export async function obterSerieEntregabilidadeEmails(): Promise<{ dias: number; serie: SerieEntregabilidadeEmailDia[] }> {
  const res = await callRpc<{ sucesso?: boolean; erro?: string; dias?: number; serie?: SerieEntregabilidadeEmailDia[] }>(
    "obterSerieEntregabilidadeEmails"
  );
  if (res?.sucesso === false) throw new Error(res.erro || "Erro ao carregar a série de entregabilidade de e-mails.");
  return { dias: res?.dias ?? 30, serie: res?.serie ?? [] };
}

export function salvarLimiteEmailsDiario(limite: number) {
  return callRpc<{ sucesso?: boolean; erro?: string; limiteEmailsDiario?: number }>("salvarLimiteEmailsDiario", limite);
}

export interface ConfiguracaoEntregaEmails {
  resendFrom: string;
  resendReplyTo: string;
  resendTestTo: string;
  ambiente: AmbienteEmail;
  publicAppUrl: string;
  resendWebhookSecret: string;
  webhookSecretConfigurado?: boolean;
  webhookSecretMask?: string;
  webhookUrl?: string;
}

export function salvarConfiguracaoEntregaEmails(dados: ConfiguracaoEntregaEmails) {
  return callRpc<{
    sucesso?: boolean;
    erro?: string;
    configuracao?: ConfiguracaoEntregaEmails;
  }>("salvarConfiguracaoEntregaEmails", dados);
}

export function salvarResendAtivo(ativo: boolean) {
  return callRpc<{ sucesso?: boolean; ativo?: boolean; erro?: string }>("salvarResendAtivo", ativo);
}

export function salvarIaAtiva(ativo: boolean) {
  return callRpc<{ sucesso?: boolean; ativo?: boolean; erro?: string }>("salvarIaAtiva", ativo);
}

export function salvarLimiteEmailsOrganizacao(codOrganizacao: number, limite: number) {
  return callRpc<{ sucesso?: boolean; erro?: string; limiteAtualizadoEm?: string }>(
    "salvarLimiteEmailsOrganizacao",
    codOrganizacao,
    limite
  );
}

export function salvarEmailsAtivoOrganizacao(codOrganizacao: number, ativo: boolean) {
  return callRpc<{ sucesso?: boolean; erro?: string; emailsAtivo?: boolean }>(
    "salvarEmailsAtivoOrganizacao",
    codOrganizacao,
    ativo
  );
}

export type TipoModeloEmail =
  | "BOAS_VINDAS"
  | "RECUPERACAO_SENHA"
  | "CHAMADO_NOVO"
  | "CHAMADO_ENCERRADO"
  | "ESTOQUE_BAIXO"
  | "NOVA_VENDA"
  | "PARCELA_ATRASADA"
  | "PAGAMENTO_CONFIRMADO"
  | "PEDIDO_INSUMO_CLIENTE"
  | "PEDIDO_SUPORTE_CLIENTE"
  | "VENCIMENTO_INSUMO"
  | "NOVA_COMPRA"
  | "ANIVERSARIO_COLABORADOR"
  | (string & {});

export interface ModeloEmail {
  id: number;
  tipo: TipoModeloEmail;
  nome: string;
  conteudoHtml: string;
  status: "ATIVO" | "INATIVO";
  favorito: boolean;
  marcadoresObrigatorios: string[];
  criadoEm: string;
  atualizadoEm: string;
}

export async function listarModelosEmail(): Promise<ModeloEmail[]> {
  const response = await callRpc<{ sucesso?: boolean; erro?: string; dados?: ModeloEmail[] }>("listarModelosEmail");
  if (response?.sucesso === false) throw new Error(response.erro || "Erro ao carregar os modelos de e-mail.");
  return response?.dados ?? [];
}

export function salvarModeloEmail(dados: Pick<ModeloEmail, "tipo" | "conteudoHtml">) {
  return callRpc<{ sucesso?: boolean; erro?: string; dados?: ModeloEmail }>("salvarModeloEmail", dados);
}

export function favoritarModeloEmail(tipo: TipoModeloEmail, favorito: boolean) {
  return callRpc<{ sucesso?: boolean; erro?: string; favorito?: boolean }>("favoritarModeloEmail", { tipo, favorito });
}

export function criarModeloEmail(dados: { nome: string; conteudoHtml: string; tipo?: string }) {
  return callRpc<{ sucesso?: boolean; erro?: string; dados?: ModeloEmail }>("criarModeloEmail", dados);
}

export function importarModelosEmail(payload: { base64: string; fileName: string }) {
  return callRpc<{ sucesso?: boolean; erro?: string; importados?: number }>("importarModelosEmail", payload);
}

/* ------------------- Imagens do sistema ------------------- */
export interface IdsImagensConfig {
  aberto?: string;
  fechado?: string;
  abertoWhite?: string;
  fechadoWhite?: string;
  loginLateral?: string;
  sistema?: string;
  favicon?: string;
  notaFiscal?: string;
  agenteIA?: string;
  emailRodape?: string;
  /** Plano de fundo da tela de abertura do aplicativo. */
  splashMobile?: string;
}
export async function obterIdsImagensConfig(): Promise<IdsImagensConfig> {
  const res = await callRpc<IdsImagensConfig>("obterIdsImagensConfig");
  return res ?? {};
}
export interface FaviconSistema {
  id?: string;
  url?: string;
}
export async function obterFaviconSistema(codEmpresa = ""): Promise<FaviconSistema> {
  const res = await callRpc<FaviconSistema>("obterFaviconSistema", codEmpresa);
  return res ?? {};
}
export function salvarImagemSistema(base64: string, tipo: string) {
  return callRpc<{ sucesso?: boolean; id?: string; url?: string }>(
    "salvarImagemSistemaServidor", base64, tipo
  );
}
export function removerImagemSistema(tipo: string) {
  return callRpc("removerImagemSistemaServidor", tipo);
}

/* ------------------- PDFs do sistema ------------------- */
export interface IdsPdfsConfig {
  termoUso?: string;
  politicaPrivacidade?: string;
  politicaCookies?: string;
  politicaCancelamento?: string;
  conhecimentoIA?: string;
}

export interface AceiteTermosUsuario {
  sucesso?: boolean;
  erro?: string;
  precisaAceitar: boolean;
  versao: string;
  aceiteEm?: string;
  documentos?: IdsPdfsConfig;
}

/** Situação do aceite dos termos do usuário logado (exigido no primeiro acesso). */
export function obterAceiteTermos(): Promise<AceiteTermosUsuario> {
  return callRpc<AceiteTermosUsuario>("obterAceiteTermos");
}

export function registrarAceiteTermos(): Promise<{ sucesso?: boolean; versao?: string }> {
  return callRpc("registrarAceiteTermos");
}
export async function obterIdsPdfsConfig(): Promise<IdsPdfsConfig> {
  const res = await callRpc<IdsPdfsConfig>("obterIdsPdfsConfig");
  return res ?? {};
}
export function salvarPdfSistema(base64: string, tipo: string) {
  return callRpc<{ sucesso?: boolean; id?: string; url?: string }>(
    "salvarPdfSistemaServidor", base64, tipo
  );
}
export function removerPdfSistema(tipo: string) {
  return callRpc("removerPdfSistemaServidor", tipo);
}

/* ------------------- Documentos do sistema (texto) ------------------- */
export type ChaveDocumentoTexto =
  | "termoUso"
  | "politicaPrivacidade"
  | "politicaCookies"
  | "politicaCancelamento";

export interface TextosDocumentosConfig {
  termoUso?: string;
  politicaPrivacidade?: string;
  politicaCookies?: string;
  politicaCancelamento?: string;
}

export async function obterTextosDocumentosConfig(): Promise<TextosDocumentosConfig> {
  const res = await callRpc<TextosDocumentosConfig>("obterTextosDocumentosConfig");
  return res ?? {};
}

export function salvarTextoDocumento(texto: string, tipo: ChaveDocumentoTexto) {
  return callRpc<{ sucesso?: boolean; erro?: string }>("salvarTextoDocumentoServidor", texto, tipo);
}

/* ------------------- Organizações ------------------- */
export interface OrganizacaoConfig {
  cod: number;
  organizacao: string;
  contato: string;
  aceiteTermos: string;
  status: string;
  dataCadastro: string;
  testeGratis: string;
  ativacao: string;
  observacao: string;
  totalEmpresas: number;
  totalUsuarios: number;
}

export async function obterOrganizacoesConfig(): Promise<OrganizacaoConfig[]> {
  const res = await callRpc<{ sucesso?: boolean; dados?: OrganizacaoConfig[] }>("obterOrganizacoesBanco");
  return res?.dados ?? [];
}

export async function obterOrganizacoesAtivasConfig(): Promise<OrganizacaoConfig[]> {
  return somenteCadastrosAtivos(await obterOrganizacoesConfig());
}

type RespostaOrganizacao = { sucesso?: boolean; erro?: string; status?: string; excluidos?: number };

export async function alterarStatusOrganizacaoConfig(codOrganizacao: number, status: "ATIVO" | "INATIVO") {
  const resposta = await callRpc<RespostaOrganizacao>("alterarStatusOrganizacaoBanco", { codOrganizacao, status });
  if (!resposta?.sucesso) throw new Error(resposta?.erro || "Não foi possível alterar o status da organização.");
  return resposta;
}

export async function excluirOrganizacaoConfig(codOrganizacao: number) {
  const resposta = await callRpc<RespostaOrganizacao>("excluirOrganizacaoBanco", { codOrganizacao });
  if (!resposta?.sucesso) throw new Error(resposta?.erro || "Não foi possível excluir a organização.");
  return resposta;
}

/* ------------------- Bloco de Notas ------------------- */
export interface Nota {
  id: string;
  titulo: string;
  texto: string;
  cor: string;
  corTexto?: string;
  favorito?: boolean | string;
  posX?: number;
  posY?: number;
  [chave: string]: unknown;
}

function limpaTexto(v: unknown): string {
  const s = v == null ? "" : String(v);
  return s === "null" || s === "undefined" ? "" : s;
}

export async function carregarNotas(): Promise<Nota[]> {
  const res = await callRpc<{ sucesso?: boolean; notas?: Nota[] }>("carregarNotasBanco");
  const notas = res?.notas ?? [];
  return notas.map((n) => ({
    ...n,
    titulo: limpaTexto(n.titulo),
    texto: limpaTexto(n.texto),
    cor: n.cor || "#fff3b0",
  }));
}

export function salvarNota(nota: Nota): Promise<{ sucesso?: boolean; erro?: string }> {
  return callRpc("salvarNotaBanco", nota);
}

export function excluirNota(notaId: string): Promise<{ sucesso?: boolean }> {
  return callRpc("excluirNotaBanco", notaId);
}
